import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MobileMenu } from "@/components/Layout/MobileMenu";
import { NavbarActions } from "@/components/Layout/NavbarActions";
import { ThemeToggle } from "@/components/Layout/ThemeToggle";
import { SearchCommandMenu } from "@/components/Search/SearchCommandMenu";
import { NotificationsPopover } from "@/components/Notifications/NotificationsPopover";
import { useAuth } from "@/context/AuthContext";
import { Search, Menu } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export function Navbar() {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSearchMenu, setShowSearchMenu] = useState(false);
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [scrollDirection, setScrollDirection] = useState("up");
  const [lastScrollY, setLastScrollY] = useState(0);
  const { user } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();
  
  const searchInputRef = useState<HTMLInputElement | null>(null);
  
  useEffect(() => {
    if (showSearchInput && searchInputRef[0]) {
      searchInputRef[0].focus();
    }
  }, [showSearchInput, searchInputRef]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".search-container") && showSearchInput) {
        setShowSearchInput(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearchInput]);
  
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (scrollY > lastScrollY) {
        setScrollDirection("down");
      } else {
        setScrollDirection("up");
      }
      setLastScrollY(scrollY);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);
  
  const isScrolled = lastScrollY > 10;
  
  const navbarClass = isMobile
    ? `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrollDirection === "down" && lastScrollY > 50 ? "-translate-y-full" : "translate-y-0"
      } ${isScrolled ? "glass-surface border-b-0" : "bg-transparent border-b border-white/10"}`
    : `fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-surface border-b-0" : "bg-transparent border-b border-white/10"
      }`;

  const isActive = (path: string) => location.pathname === path;
  
  return (
    <header className={navbarClass}>
      <div className="container mx-auto px-4">
        <div className="flex h-14 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img src="/lovable-uploads/696668bf-afe7-4791-9555-262f2b681308.png" alt="ThePromptSpace Logo" className="h-7 w-auto" />
          </Link>
          
          {/* Desktop Navigation Links */}
          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/prompts"
              className={cn(
                "px-3 py-2 text-sm font-medium rounded-md transition-colors duration-300",
                isActive("/prompts") 
                  ? "text-[#00C2FF] bg-[#00C2FF]/10" 
                  : "text-white/70 hover:text-white hover:bg-white/5"
              )}
            >
              Explore Prompts
            </Link>
            <Link
              to="/groups"
              className={cn(
                "px-3 py-2 text-sm font-medium rounded-md transition-colors duration-300",
                isActive("/groups") 
                  ? "text-[#00C2FF] bg-[#00C2FF]/10" 
                  : "text-white/70 hover:text-white hover:bg-white/5"
              )}
            >
              Groups
            </Link>
            <Link
              to="/blog"
              className={cn(
                "px-3 py-2 text-sm font-medium rounded-md transition-colors duration-300",
                isActive("/blog") 
                  ? "text-[#00C2FF] bg-[#00C2FF]/10" 
                  : "text-white/70 hover:text-white hover:bg-white/5"
              )}
            >
              Blog
            </Link>
          </nav>
          
          {/* Center: Search */}
          <div className="flex-1 flex justify-center search-container relative max-w-md mx-4">
            {showSearchInput ? (
              <div className="absolute z-10 w-full bg-background border border-white/10 rounded-md shadow-lg animate-in fade-in-0 zoom-in-95">
                <Input 
                  ref={(ref) => searchInputRef[0] = ref}
                  className="w-full h-9 bg-transparent border-0"
                  placeholder="Search prompts..."
                  onKeyDown={(e) => {
                    if(e.key === 'Enter') {
                      setShowSearchMenu(true);
                      setShowSearchInput(false);
                    }
                    if(e.key === 'Escape') {
                      setShowSearchInput(false);
                    }
                  }}
                />
              </div>
            ) : (
              <Button 
                variant="ghost" 
                size="sm"
                className="hidden md:flex text-white/60 hover:text-white"
                onClick={() => setShowSearchInput(true)}
              >
                <Search className="h-4 w-4 mr-1" />
                <span className="text-xs">Search</span>
              </Button>
            )}
          </div>
          
          {/* Right: CTA, Notifications, Theme, Actions */}
          <div className="flex items-center gap-2">
            {user && <NotificationsPopover />}
            <ThemeToggle />
            <NavbarActions />
            
            {/* Hamburger menu - visible on all screen sizes */}
            <Button
              variant="ghost"
              size="icon"
              className="text-white/70 hover:text-white"
              onClick={() => setShowMobileMenu(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
      
      <MobileMenu 
        open={showMobileMenu}
        onClose={() => setShowMobileMenu(false)} 
      />
      
      <SearchCommandMenu 
        open={showSearchMenu} 
        onOpenChange={setShowSearchMenu} 
        onClose={() => setShowSearchMenu(false)}
      />
    </header>
  );
}
